import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../localization/out/messages.js';
import { getPudoLocations } from '../../api/colleague-ticket-queries.js';
import { getServiceDeskRecipients } from '../../api/service-desk-queries.js';
import { getMyTickets } from '../../api/ticket-queries.js';
import { getMyPartnerCompany } from '../../api/user-queries.js';
import { router } from '../startup.js';
import { ActionsMenu, showsDellLinks } from './_actions-menu.js';
import { etaWindowLabel } from './_eta-window.js';
import { ReassignmentNotice } from './_reassignment-notice.js';
import { ServiceDeskButton, TicketCard } from './_ticket-card.js';
import { TicketsMapView } from './_tickets-map-view.js';

// Tickets are fetched, searched, filtered and sorted server-side (SN `ticket_list`), one page at a
// time — so search and paging span the whole instance, not just a first page held in the browser.
const PAGE_SIZE = 50;

// ── Chip flow ordering ──────────────────────────────────────────────
// The status chips are ordered by the Work Order Task flow (active work first). Counts are gone:
// SN returns no total, so we can't count beyond the loaded page without N extra queries.

/** WOT substate 8 = On Route (spec §6.3) — sorts between Accepted (17) and Work in Progress (18). */
const SUBSTATE_ON_ROUTE = '8';

const STATE_FLOW_RANK: Record<string, number> = {
  '16': 0, // Assigned
  '17': 1, // Accepted (On Route → 1.5)
  '18': 2, // Work in Progress
  '12': 3, // Scheduled
  '10': 4, // Pending Dispatch
  '1': 5, // Draft
  '19': 6.5, // Resolved (FE work done, awaiting close) — sinks just above the closed bucket
  '4': 7, // Closed Incomplete
  '3': 8, // Closed Complete
};
const UNKNOWN_FLOW_RANK = 6;

/** Flow position of a state code, used to order the filter chips. */
function stateRank(state: string | null | undefined): number {
  return STATE_FLOW_RANK[state ?? ''] ?? UNKNOWN_FLOW_RANK;
}

// ── Status filters ──────────────────────────────────────────────────
// Chips are server-side filters: selecting one re-queries SN for that status. Because there's no
// total/aggregate endpoint, the available chips are discovered from the statuses seen while paging
// (accumulated for the session) rather than known up front — labels reuse SN's own display values,
// so no extra localization is needed. "All" is always present so a filter can be cleared.

const ALL_FILTER = 'all';
const stateFilterKey = (code: string) => `state:${code}`;
const substateFilterKey = (code: string) => `substate:${code}`;

interface FilterChip {
  /** Stable key — also what's persisted to localStorage: 'all' | `state:<code>` | `substate:<code>`. */
  key: string;
  label: string;
  /** Flow position used to order the chips. */
  rank: number;
}

/** Split a chip key into the server query params it represents. */
function parseFilter(key: string): { state?: string; substate?: string } {
  if (key.startsWith('state:')) return { state: key.slice('state:'.length) };
  if (key.startsWith('substate:')) return { substate: key.slice('substate:'.length) };
  return {};
}

// ── Filter persistence ──────────────────────────────────────────────
// Persist the picked chip per-browser so navigating into a ticket and back (a fresh mount of
// this page) restores the same filter instead of snapping back to "All".
const FILTER_PREF_KEY = 'smartteams-app:ticket-overview-filter';

function readStoredFilter(): string {
  if (typeof window === 'undefined') return ALL_FILTER;
  try {
    return window.localStorage.getItem(FILTER_PREF_KEY) ?? ALL_FILTER;
  } catch {
    return ALL_FILTER;
  }
}

function writeStoredFilter(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FILTER_PREF_KEY, key);
  } catch {
    // private-mode Safari can throw; ignore — the in-memory selection still works.
  }
}

export const TicketOverviewPage = stateful(() => {
  // Raw input drives the box; `search` is the debounced value that actually queries the server.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Active filter chip key ('all' | state:<code> | substate:<code>), restored from localStorage
  // so it survives navigating into a ticket and back.
  const [activeFilter, setActiveFilter] = useState<string>(readStoredFilter());
  const [page, setPage] = useState(0);
  // List or map of the tickets on this page — same switch as the Colleagues view.
  const [view, setView] = useState<'list' | 'map'>('list');
  // Statuses seen so far this session, kept available as filter chips while paging.
  const [discoveredChips, setDiscoveredChips] = useState<FilterChip[]>([]);

  // Debounce the search box → server query, 300ms after the last keystroke. A changed term also
  // jumps back to the first page (the old offset is meaningless against new results).
  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== search) {
        setSearch(next);
        setPage(0);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { state: filterState, substate: filterSubstate } = parseFilter(activeFilter);

  // Who the "Email service desk" button addresses (set on the partner company; empty opens the mail with the address left blank).
  const recipientsQuery = useQuery(() => getServiceDeskRecipients()({ to: 1, cc: 1 }), []);
  const recipients = recipientsQuery.value ?? null;

  // PUDO depots (map pins and the Actions menu) and the company that decides whether the Dell links show. Both are
  // independent of the filters, so they load once.
  const pudosQuery = useQuery(
    () =>
      getPudoLocations()({
        sysId: 1,
        name: 1,
        company: 1,
        street: 1,
        zipCode: 1,
        city: 1,
        siteAccessHours: 1,
        lat: 1,
        lng: 1,
      }),
    [],
  );
  const myCompanyQuery = useQuery(() => getMyPartnerCompany()({ _id: 1, name: 1 }), []);
  const pudos = pudosQuery.value ?? [];

  const ticketsQuery = useQuery(
    () =>
      getMyTickets(search, filterState, filterSubstate, PAGE_SIZE, page * PAGE_SIZE)({
        number: 1,
        title: 1,
        assignedToName: 1,
        state: 1,
        stateLabel: 1,
        substate: 1,
        substateLabel: 1,
        etaWindow: 1,
        locationLabel: 1,
        locationLat: 1,
        locationLng: 1,
        correlationId: 1,
        clientName: 1,
        contactPhone: 1,
        expectedStart: 1,
        expectedStartDisplay: 1,
        windowStart: 1,
        windowEnd: 1,
        fulfilmentStatusLabel: 1,
        // Read by the shared card (parts icon) and the map popups (reply-code flag). Mostly null for now, but
        // the entity throws on a field that was not selected.
        partsStatusLabel: 1,
        replyCode: 1,
        replyCodeMeaning: 1,
        replyCodeDisplay: 1,
        workNotes: { date: 1, html: 1 },
        parts: {
          modelNumber: 1,
          quantity: 1,
        },
      }),
    [search, filterState, filterSubstate, page],
  );

  // Accumulate the statuses present in each loaded page so they stay selectable as chips.
  useEffect(() => {
    const loaded = ticketsQuery.value;
    if (!loaded || loaded.length === 0) return;
    const map = new Map(discoveredChips.map(c => [c.key, c]));
    let changed = false;
    for (const t of loaded) {
      if (t.state) {
        const key = stateFilterKey(t.state);
        if (!map.has(key)) {
          map.set(key, { key, label: t.stateLabel || t.state, rank: stateRank(t.state) });
          changed = true;
        }
      }
      // Mirror the card: the only substate the app surfaces is On Route.
      if (t.substate === SUBSTATE_ON_ROUTE && t.substateLabel) {
        const key = substateFilterKey(SUBSTATE_ON_ROUTE);
        if (!map.has(key)) {
          map.set(key, { key, label: t.substateLabel, rank: 1.5 });
          changed = true;
        }
      }
    }
    if (changed) setDiscoveredChips([...map.values()].sort((a, b) => a.rank - b.rank));
  }, [ticketsQuery.value]);

  const selectFilter = (key: string) => {
    setActiveFilter(key);
    writeStoredFilter(key);
    setPage(0);
  };

  const tickets = ticketsQuery.value ?? [];
  const chips: FilterChip[] = [{ key: ALL_FILTER, label: m.ticket_filter_all(), rank: -1 }, ...discoveredChips];
  // Always surface the active filter — e.g. restored from storage before its page loaded, or it
  // currently matches nothing — so it shows as selected and "All" stays available to clear it.
  // The label falls back to the raw code until a loaded page supplies SN's display label.
  if (activeFilter !== ALL_FILTER && !chips.some(c => c.key === activeFilter)) {
    const { state, substate } = parseFilter(activeFilter);
    chips.push({ key: activeFilter, label: state ?? substate ?? activeFilter, rank: substate ? 1.5 : stateRank(state) });
    chips.sort((a, b) => a.rank - b.rank);
  }
  const hasPrev = page > 0;
  // No total from SN: a full page means there may be more; a short page is the last one.
  const hasNext = tickets.length === PAGE_SIZE;
  const showPager = hasPrev || hasNext;

  const listRegion = () => {
    if (ticketsQuery.loading && tickets.length === 0) {
      return html`
        <div class="flex items-center justify-center flex-1">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>
      `;
    }
    if (ticketsQuery.error) {
      return html`
        <div class="flex items-center justify-center flex-1 text-error">
          <p>${m.error_generic()}</p>
        </div>
      `;
    }
    if (tickets.length === 0) {
      return html`
        <div class="flex flex-col items-center justify-center flex-1 text-base-content/50 gap-2 py-8">
          <span class="icon-[material-symbols--confirmation-number-outline] text-4xl"></span>
          <p>${m.ticket_empty()}</p>
        </div>
      `;
    }
    return html`
      <div class="overflow-y-auto flex-1 min-h-0 flex flex-col gap-2">
        ${tickets.map(ticket =>
          TicketCard(ticket, {
            href: router.link('ticketDetail', { params: { number: ticket.number! } }),
            showDate: true,
            footer: ServiceDeskButton(ticket, recipients, { overlay: true }),
            badgeExtra:
              ticket.substate === SUBSTATE_ON_ROUTE && ticket.substateLabel
                ? html`<span class="badge badge-sm badge-outline badge-info">${ticket.substateLabel}</span>`
                : nothing,
            infoExtra: ticket.etaWindow
              ? html`
                  <div class="flex items-center gap-1 text-base-content/70 font-medium">
                    <span class="icon-[material-symbols--av-timer] text-sm"></span>
                    <span>${m.eta_window_card_title()}: ${etaWindowLabel(ticket.etaWindow)}</span>
                  </div>
                `
              : nothing,
          }),
        )}
      </div>
    `;
  };

  return html`
    <div class="p-4 flex flex-col gap-4 h-full">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <h1 class="text-2xl font-bold">${m.ticket_overview_title()}</h1>
          <p class="text-sm text-base-content/60">${m.ticket_overview_subtitle()}</p>
        </div>
        ${ActionsMenu({
          tickets,
          pudos,
          showDellLinks: showsDellLinks(myCompanyQuery.value?.name),
          csvFilename: 'my-tickets-addresses.csv',
        })}
      </div>

      ${ReassignmentNotice()}

      <!-- Search — debounced, runs server-side across the whole instance (number / title / engineer). -->
      <label class="input input-bordered flex items-center gap-2">
        <span class="icon-[material-symbols--search] text-base-content/40"></span>
        <input
          type="text"
          class="grow"
          placeholder="${m.ticket_search_placeholder()}"
          .value=${searchInput}
          @input=${(e: InputEvent) => setSearchInput((e.target as HTMLInputElement).value)}
        />
        ${searchInput
          ? html`
              <button class="btn btn-ghost btn-xs btn-circle" @click=${() => setSearchInput('')}>
                <span class="icon-[material-symbols--close] text-sm"></span>
              </button>
            `
          : nothing}
      </label>

      <!-- Status filters — server-side; one chip per WOT status seen so far (label matches the card
           badge), in flow order, plus an On Route substate chip and All. Hidden until more than one
           status is available. -->
      ${chips.length > 1
        ? html`
            <div class="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
              ${chips.map(chip => {
                const selected = chip.key === activeFilter;
                return html`
                  <button
                    type="button"
                    class="btn btn-sm shrink-0 ${selected ? 'btn-primary' : 'btn-outline'}"
                    aria-pressed=${selected}
                    @click=${() => selectFilter(chip.key)}
                  >
                    ${chip.label}
                  </button>
                `;
              })}
            </div>
          `
        : nothing}

      <!-- List or map of the tickets on this page (the map adds the ServiceNow PUDO depots). -->
      <div class="join self-start">
        <button
          class="btn btn-sm join-item gap-1 ${view === 'list' ? 'btn-primary' : 'btn-outline'}"
          aria-pressed=${view === 'list'}
          @click=${() => setView('list')}
        >
          <span class="icon-[material-symbols--format-list-bulleted] text-base"></span>
          ${m.colleague_view_list()}
        </button>
        <button
          class="btn btn-sm join-item gap-1 ${view === 'map' ? 'btn-primary' : 'btn-outline'}"
          aria-pressed=${view === 'map'}
          @click=${() => setView('map')}
        >
          <span class="icon-[material-symbols--map-outline] text-base"></span>
          ${m.colleague_view_map()}
        </button>
      </div>

      <!-- Ticket list -->
      ${view === 'list' ? listRegion() : TicketsMapView({ tickets, pudos, recipients })}

      <!-- Pagination — Prev / page / Next. SN returns no total, so there's no jump-to-page: Next is
           disabled once a page comes back short (fewer than PAGE_SIZE rows). -->
      ${showPager
        ? html`
            <div class="flex items-center justify-center gap-4 pt-1">
              <button
                type="button"
                class="btn btn-sm btn-circle btn-outline"
                aria-label="Previous page"
                ?disabled=${!hasPrev || ticketsQuery.loading}
                @click=${() => setPage(Math.max(0, page - 1))}
              >
                <span class="icon-[material-symbols--chevron-left]"></span>
              </button>
              <span class="text-sm tabular-nums text-base-content/70">${page + 1}</span>
              <button
                type="button"
                class="btn btn-sm btn-circle btn-outline"
                aria-label="Next page"
                ?disabled=${!hasNext || ticketsQuery.loading}
                @click=${() => setPage(page + 1)}
              >
                <span class="icon-[material-symbols--chevron-right]"></span>
              </button>
            </div>
          `
        : nothing}
    </div>
  `;
});
